import { ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { MAIL_SUMMARY_KEY, fetchMailSummary, type MailSummaryDto } from "../../api/mail.js";
import {
  ControlHead,
  ControlFreshBadge,
  ControlMono,
  ControlNote,
  ControlNotMeasurable,
  ControlPanel,
  ControlRow,
  GRID_COLUMN_STYLE,
  GRID_MAIN_CLASS,
  formatInteger,
  type ControlTone,
} from "../control/ControlPrimitives.js";

const QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: false,
} as const;

function deliveryTone(failures: number): ControlTone {
  return failures === 0 ? "ok" : "warn";
}

function deliveryLabel(
  total: number,
  failures: number,
  deliveredKey:
    | "admin.gtcc.mail.view.delivery_all_delivered"
    | "admin.gtcc.mail.view.delivery_delivered",
  t: (key: string, values?: Record<string, string>) => string,
  locale: Parameters<typeof formatInteger>[1],
): string {
  const count = formatInteger(total, locale);
  return failures === 0
    ? t(deliveredKey, { count })
    : t("admin.gtcc.mail.view.delivery_count", {
        count,
        failures: formatInteger(failures, locale),
      });
}

function valueOrUnknown(value: string | null, unknown: string): ReactNode {
  return value === null ? (
    <ControlNotMeasurable label={unknown} />
  ) : (
    <ControlMono>{value}</ControlMono>
  );
}

function isMailSummary(value: unknown): value is MailSummaryDto {
  if (typeof value !== "object" || value === null) return false;
  const summary = value as Partial<MailSummaryDto>;
  const deliveries = summary.deliveries30d;
  return (
    (summary.provider === "log-only" ||
      summary.provider === "smtp" ||
      summary.provider === "postmark") &&
    (summary.configuredSender === null || typeof summary.configuredSender === "string") &&
    Array.isArray(summary.templateLocales) &&
    summary.templateLocales.every((locale) => typeof locale === "string") &&
    typeof deliveries === "object" &&
    deliveries !== null &&
    typeof deliveries.measuredAt === "string" &&
    typeof deliveries.freshnessSeconds === "number" &&
    Number.isInteger(deliveries.freshnessSeconds) &&
    deliveries.freshnessSeconds >= 0 &&
    typeof deliveries.invitations?.total === "number" &&
    typeof deliveries.invitations?.failures === "number" &&
    typeof deliveries.account?.total === "number" &&
    typeof deliveries.account?.failures === "number" &&
    typeof deliveries.affectedRecipients === "number" &&
    typeof deliveries.spamComplaints === "number" &&
    typeof deliveries.rejected === "number"
  );
}

function MailPanels({ data }: { readonly data: MailSummaryDto }) {
  const { t, locale } = useLocale();
  const { deliveries30d } = data;
  const invitationTone = deliveryTone(deliveries30d.invitations.failures);
  const accountTone = deliveryTone(deliveries30d.account.failures);
  const suppressionTone: ControlTone = deliveries30d.affectedRecipients === 0 ? "ok" : "warn";
  const locales =
    data.templateLocales.length === 0
      ? null
      : data.templateLocales.map((localeCode) => localeCode.toUpperCase()).join(" · ");
  return (
    <div className={GRID_MAIN_CLASS}>
      <div style={GRID_COLUMN_STYLE}>
        <ControlPanel title={t("admin.gtcc.mail.view.product.title")}>
          <Stack gap={0}>
            <ControlRow
              testId="gtcc-mail-invitations"
              tone={invitationTone}
              label={t("admin.gtcc.mail.view.invitations")}
              value={
                <ControlMono>
                  {deliveryLabel(
                    deliveries30d.invitations.total,
                    deliveries30d.invitations.failures,
                    "admin.gtcc.mail.view.delivery_all_delivered",
                    t,
                    locale,
                  )}
                </ControlMono>
              }
            />
            <ControlRow
              testId="gtcc-mail-account"
              tone={accountTone}
              label={t("admin.gtcc.mail.view.account")}
              value={
                <ControlMono>
                  {deliveryLabel(
                    deliveries30d.account.total,
                    deliveries30d.account.failures,
                    "admin.gtcc.mail.view.delivery_delivered",
                    t,
                    locale,
                  )}
                </ControlMono>
              }
            />
            <ControlRow
              testId="gtcc-mail-sender"
              tone={data.configuredSender === null ? "muted" : "ok"}
              label={t("admin.gtcc.mail.view.sender")}
              value={valueOrUnknown(
                data.configuredSender,
                t("admin.gtcc.mail.view.not_measurable"),
              )}
            />
            <ControlRow
              testId="gtcc-mail-locales"
              tone={locales === null ? "muted" : "info"}
              label={t("admin.gtcc.mail.view.locales")}
              value={valueOrUnknown(locales, t("admin.gtcc.mail.view.not_measurable"))}
            />
          </Stack>
        </ControlPanel>
      </div>
      <div style={GRID_COLUMN_STYLE}>
        <ControlPanel title={t("admin.gtcc.mail.view.suppression.title")}>
          <Stack gap={0}>
            <ControlRow
              testId="gtcc-mail-affected"
              tone={suppressionTone}
              label={t("admin.gtcc.mail.view.affected", {
                count: formatInteger(deliveries30d.affectedRecipients, locale),
              })}
              value={<ControlMono>{t("admin.gtcc.mail.view.auto_retry")}</ControlMono>}
            />
            <ControlRow
              testId="gtcc-mail-complaints"
              tone={deliveries30d.spamComplaints === 0 ? "ok" : "warn"}
              label={t("admin.gtcc.mail.view.complaints")}
              value={
                <ControlMono>{formatInteger(deliveries30d.spamComplaints, locale)}</ControlMono>
              }
            />
            <ControlNote testId="gtcc-mail-suppression-note">
              {t("admin.gtcc.mail.view.suppression_note")}
            </ControlNote>
          </Stack>
        </ControlPanel>
      </div>
    </div>
  );
}

export function MailView(): ReactNode {
  const { t, locale } = useLocale();
  const query = useQuery({
    queryKey: MAIL_SUMMARY_KEY,
    queryFn: ({ signal }) => fetchMailSummary(signal),
    ...QUERY_OPTIONS,
  });
  const data = query.data?.ok && isMailSummary(query.data.data) ? query.data.data : null;
  return (
    <Stack gap={3} data-testid="gtcc-view-mail-summary">
      <ControlHead
        testId="gtcc-mail-head"
        title={t("admin.gtcc.mail.view.title")}
        subtitle={t("admin.gtcc.mail.view.subtitle")}
        fresh={
          data === null ? undefined : (
            <ControlFreshBadge
              testId="gtcc-mail-fresh"
              text={t("admin.gtcc.cockpit.infra.fresh.seconds", {
                seconds: formatInteger(data.deliveries30d.freshnessSeconds, locale),
              })}
            />
          )
        }
      />
      {query.isPending ? (
        <LoadingState label={t("common.loading")} />
      ) : data !== null ? (
        <MailPanels data={data} />
      ) : (
        <ErrorState
          severity={query.data?.ok === false && query.data.kind === "forbidden" ? "warn" : "error"}
          title={t(
            query.data?.ok === false && query.data.kind === "forbidden"
              ? "admin.gtcc.mail.view.forbidden"
              : "admin.gtcc.mail.view.error",
          )}
          body={t("admin.gtcc.mail.view.error_body")}
        />
      )}
    </Stack>
  );
}
